import type {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { HttpResponse } from '@angular/common/http';
import { DestroyRef, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { Observable } from 'rxjs';
import { Subject, throwError } from 'rxjs';
import { catchError, delay, take, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

@Service({ autoProvided: false })
/**
 * HTTP interceptor to limit concurrent outgoing HTTP requests.
 *
 * Ensures that no more than a fixed number of requests are in-flight at the same time
 * by queueing additional requests and releasing them as responses complete. This helps
 * avoid hitting browser per-host connection limits.
 *
 * Max concurrency is set to 5 to keep one slot free for static assets downloads.
 *
 * @example
 * // In an Angular module/provider config
 * { provide: HTTP_INTERCEPTORS, useClass: ConcurrenceInterceptor, multi: true }
 */
export class ConcurrenceInterceptor implements HttpInterceptor {
  private readonly destroyRef = inject(DestroyRef);

  // We use 5 to keep a slot for assets (Chrome max 6 medium.com/@hnasr/chromes-6-tcp-connections-limit-c199fe550af6)
  private readonly maxConcurrentRequests = 5;
  private pendingRequestCount = 0;
  private readonly pendingRequestsQueue: {
    reqID: string;
    request: HttpRequest<unknown>;
  }[] = [];
  private readonly responseSubjectObject = new Map<
    string,
    Subject<HttpEvent<unknown>>
  >();

  /**
   * Intercept outgoing HTTP requests and apply concurrency limiting.
   *
   * If the number of in-flight requests reaches the configured maximum, the request
   * is queued and its response is emitted later via a Subject when processed.
   *
   * @param request The outgoing HTTP request. {@link HttpRequest}
   * @param next The next HTTP handler in the interceptor chain. {@link HttpHandler}
   * @returns An observable of {@link HttpEvent} for the request/response stream.
   */
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.pendingRequestCount >= this.maxConcurrentRequests) {
      const reqID: string = uuidv4();

      // Push the new request into the pending QUEUE
      this.pendingRequestsQueue.push({
        reqID,
        request: request,
      });

      const subject = new Subject<HttpEvent<unknown>>();
      this.responseSubjectObject.set(reqID, subject);

      return subject.asObservable();
    }

    // Increment the pending request count
    this.pendingRequestCount++;

    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.sendRequestFromQueue(next);
        }
      }),
      catchError((err: unknown) => {
        this.sendRequestFromQueue(next);

        return throwError(() => err);
      })
    );
  }

  /**
   * Remove and delete the Subject associated with the queued request id.
   * @param reqID The unique request identifier.
   */
  private cleanSubject(reqID: string) {
    this.responseSubjectObject.delete(reqID);
  }

  /**
   * Subscribe to the Subject to clean it after it emits next/error/complete for the given request id.
   * Uses delay(0) to allow subscribers to register before cleanup.
   * @param reqID The unique request identifier.
   * @param event The triggering event which could be a response or error.
   */
  private listenToSubjectToClean(reqID: string, event: unknown) {
    const subject = this.responseSubjectObject.get(reqID);
    if (event instanceof HttpResponse && subject) {
      // Review: confirm this is the best cleanup timing for the subject.
      // Clean subject next time it fires. Use delay to let time from other subscribers to register.
      // ! Will delete subject early if other subscriber uses other longer delay methods.
      subject
        .pipe(take(1), delay(0), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.cleanSubject(reqID);
          },
          error: () => {
            this.cleanSubject(reqID);
          },
          complete: () => {
            this.cleanSubject(reqID);
          },
        });
    }
  }

  /**
   * Process the next queued request immediately through the next handler.
   *
   * Emits all events to the queued request's Subject and triggers queue advance
   * on response or error.
   *
   * @param request The queued HTTP request. {@link HttpRequest}
   * @param next The downstream HTTP handler. {@link HttpHandler}
   * @param reqID The unique request identifier associated with the Subject.
   */
  private processNextRequest(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    reqID: string
  ): void {
    next.handle(request).subscribe({
      next: (event) => {
        if (event instanceof HttpResponse) {
          this.sendRequestFromQueue(next);
        }

        this.listenToSubjectToClean(reqID, event);
        this.responseSubjectObject.get(reqID)?.next(event);
      },
      error: (error: unknown) => {
        this.listenToSubjectToClean(reqID, error);
        this.responseSubjectObject.get(reqID)?.error(error);
      },
    });
  }

  // Function to Check Queue and Send REQUEST FROM Queue
  /**
   * Advance the queue by decrementing the pending count and processing the next request if any.
   * @param next The downstream HTTP handler. {@link HttpHandler}
   */
  private sendRequestFromQueue(next: HttpHandler) {
    // If request has responded, decrement the pending request count
    this.pendingRequestCount--;
    // Process the pending queue if there are any requests
    if (this.pendingRequestsQueue.length > 0) {
      const nextOne = this.pendingRequestsQueue.shift();

      if (nextOne) {
        this.processNextRequest(nextOne.request, next, nextOne.reqID);
      }
    }
  }
}
